import { type FocusEvent, type KeyboardEvent, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MAX_TITLE_LENGTH } from './consts';
import {
    createEditColumnFormSchema,
    TEditColumnFormInput,
    TEditColumnFormState,
} from './editColumnForm.schema';

type UseEditColumnFormParams = {
    defaultColor: string;
    defaultTitle: string;
    skipSubmitWhenUnchanged: boolean;
    onClose: () => void;
    onSubmit: (data: TEditColumnFormState) => Promise<void>;
};

export const useEditColumnForm = ({
    defaultColor,
    defaultTitle,
    skipSubmitWhenUnchanged,
    onClose,
    onSubmit,
}: UseEditColumnFormParams) => {
    const { t } = useTranslation();
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
    const isSubmittingRef = useRef(false);
    const editColumnFormSchema = createEditColumnFormSchema(
        t('crm.deals.board.column.validation.titleMaxLength', {
            max: MAX_TITLE_LENGTH,
        })
    );
    const {
        control,
        formState: { isDirty },
        handleSubmit,
    } = useForm<
        TEditColumnFormInput,
        unknown,
        TEditColumnFormState
    >({
        resolver: zodResolver(editColumnFormSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            color: defaultColor,
            title: defaultTitle,
        },
    });
    const isFormUnchanged = (values: TEditColumnFormInput) =>
        values.color === defaultColor && values.title.trim() === defaultTitle.trim();

    const handleFormSubmit: SubmitHandler<TEditColumnFormState> = async (data) => {
        if (isSubmittingRef.current) {
            return;
        }

        if (skipSubmitWhenUnchanged && isFormUnchanged(data)) {
            onClose();
            return;
        }

        isSubmittingRef.current = true;

        try {
            await onSubmit(data);
        } catch {
            // Ошибка отображается в mutation hook, форму оставляем доступной для повторной отправки.
        } finally {
            isSubmittingRef.current = false;
        }
    };
    const submitForm = handleSubmit(handleFormSubmit);

    const handleFormKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            onClose();
            return;
        }

        if (event.key !== 'Enter' || isColorPickerOpen) {
            return;
        }

        event.preventDefault();
        void submitForm();
    };

    const handleFormBlur = (event: FocusEvent<HTMLFormElement>) => {
        const { currentTarget, relatedTarget: nextFocusedElement } = event;

        if (
            isColorPickerOpen ||
            (nextFocusedElement instanceof Node && currentTarget.contains(nextFocusedElement))
        ) {
            return;
        }

        void submitForm();
    };

    return {
        control,
        handleFormBlur,
        handleFormKeyDown,
        isColorPickerOpen,
        isDirty,
        setIsColorPickerOpen,
        submitForm,
    };
};
